/**
 * Regras do formulário de campanhas, separadas da página para poderem ser
 * testadas como funções puras.
 *
 * O que mora aqui é exatamente o que erra em silêncio quando fica dentro do JSX:
 * o default de `active`, a conversão de fuso e a lista de campos que vão para a
 * API. Um rótulo novo na tela nunca deveria mudar o payload, e é isso que os
 * testes desta camada travam.
 */

// Campanha nova nasce DESATIVADA. Antes o default era `active: true`, e um
// Salvar sem querer publicava direto na vitrine.
export const CAMPANHA_VAZIA = {
    title: '',
    subtitle: '',
    marketplace: '',
    banner_image: '',
    starts_at: '',
    ends_at: '',
    priority: 0,
    active: false,
    target_type: 'internal',
    target_value: '/',
    show_countdown: false,
    source: 'manual',
};

// Campos que o formulário controla. Serve de trava: o que não está aqui não é
// enviado, então rótulo, prévia e texto de ajuda não têm como virar payload.
export const CAMPOS_ENVIADOS = Object.keys(CAMPANHA_VAZIA);

// `datetime-local` fala horário local e o backend guarda UTC. As duas conversões
// existem para o admin ver a hora que digitou, não a hora deslocada pelo fuso.
export const paraInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

export const paraISO = (local) => {
    if (!local) return null;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Contador marcado sem hora de fim é combinação impossível: o backend não
 * entrega `show_countdown` sem `ends_at`, e inventar um horário aqui seria
 * mentir na vitrine. O formulário recusa em vez de salvar algo invisível.
 */
export const contadorSemFim = (dados) =>
    Boolean(dados?.show_countdown) && !dados?.ends_at;

/** Corpo enviado ao POST/PATCH, já com fuso e tipos resolvidos. */
export function montaCorpoCampanha(dados) {
    const corpo = {};
    CAMPOS_ENVIADOS.forEach((k) => { corpo[k] = dados?.[k]; });
    corpo.starts_at = paraISO(dados?.starts_at);
    corpo.ends_at = paraISO(dados?.ends_at);
    corpo.priority = Number(dados?.priority) || 0;
    corpo.active = Boolean(dados?.active);
    corpo.show_countdown = Boolean(dados?.show_countdown);
    return corpo;
}

// Recomendação de imagem derivada da área REAL onde a campanha aparece.
// Desde a fase visual 1 (2026-09) a campanha é o HERO da Home, não mais um card
// de 340x130: largura total do container, 380 px de altura no desktop, recorte
// ao centro. O texto da campanha é desenhado pelo site à esquerda, então o
// assunto da imagem deve ficar à direita. Referência: HeroCampanhas, em
// components/PromotionsHub.jsx.
export const BANNER_LARGURA = 2560;
export const BANNER_ALTURA = 680;
export const BANNER_DICA = `Aparece como destaque no topo da Home, até 1440×380 px no desktop, recortada ao centro e mais estreita no celular. O título entra à esquerda: deixe o assunto da imagem à direita. Ideal: ${BANNER_LARGURA}×${BANNER_ALTURA} px.`;

/** Fuso do navegador de quem cadastra. Só para o aviso; não entra em conta. */
export function fusoLocal(data = new Date()) {
    try {
        const min = -data.getTimezoneOffset();
        const sinal = min < 0 ? '-' : '+';
        const h = Math.floor(Math.abs(min) / 60);
        const m = Math.abs(min) % 60;
        return `UTC${sinal}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
    } catch {
        return 'horário local';
    }
}

/**
 * Linha de detalhe de uma campanha na lista do admin ("prioridade 2 · Shopee ·
 * destino /busca?..."). Monta só com o que existe: valor ausente, vazio ou que
 * não é texto/número some da linha, em vez de virar "undefined" ou "[object Object]".
 * O backend sempre manda target_url (no mínimo "/"), mas a tela não depende disso.
 */
const textoValido = (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

export function detalheCampanha(c = {}) {
    const partes = [];
    if (typeof c.priority === 'number' && Number.isFinite(c.priority)) partes.push(`prioridade ${c.priority}`);
    const loja = textoValido(c.marketplace_name);
    if (loja) partes.push(loja);
    const destino = textoValido(c.target_url);
    partes.push(destino ? `destino ${destino}` : 'destino não definido');
    return partes.join(' · ');
}
